import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, CreditCard, X } from 'lucide-react';

import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { paymentMethodApi } from '../lib/api/paymentMethodApi';
import { AdminPaymentMethodSummary } from '../lib/api/types';

interface PaymentMethodSelectorProps {
  selectedPayerId?: string;
  selectedPaymentMethodId?: string;
  onPaymentMethodSelect: (paymentMethod: AdminPaymentMethodSummary | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedPayerId,
  selectedPaymentMethodId,
  onPaymentMethodSelect,
  label = 'Payment Method',
  placeholder = 'Select a payment method',
  required = false,
  error,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch payment methods for selected payer
  const {
    data: paymentMethodsData,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['paymentMethods', selectedPayerId],
    queryFn: () => paymentMethodApi.adminListPaymentMethodsByPayerId(selectedPayerId!),
    enabled: !!selectedPayerId,
  });

  const paymentMethods = paymentMethodsData?.PaymentMethods || [];
  const selectedPaymentMethod = paymentMethods.find(
    method => method.PaymentMethodId === selectedPaymentMethodId
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear selection when payer changes
  useEffect(() => {
    if (selectedPaymentMethodId && !selectedPaymentMethod) {
      onPaymentMethodSelect(null);
    }
  }, [selectedPayerId, selectedPaymentMethodId, selectedPaymentMethod, onPaymentMethodSelect]);

  const handlePaymentMethodSelect = (paymentMethod: AdminPaymentMethodSummary) => {
    onPaymentMethodSelect(paymentMethod);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    onPaymentMethodSelect(null);
    setIsOpen(false);
  };

  const getPaymentMethodTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      AMEX: 'Amex',
      DinersClub: 'Diners Club',
      CarteBancaire: 'Carte Bancaire',
      ProPayToProPay: 'ProPay to ProPay',
    };
    return map[type] ?? type;
  };

  if (!selectedPayerId) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <Button
          type="button"
          variant="outline"
          className="w-full justify-between"
          disabled={true}
        >
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="text-gray-500">Select a payer first</span>
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  if (queryError) {
    return (
      <div className="space-y-2">
        {label && (
          <label className="text-sm font-medium text-gray-700">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="text-sm text-red-600">
          Error loading payment methods: {queryError instanceof Error ? queryError.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={`w-full justify-between ${error ? 'border-red-500' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled || isLoading}
        >
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            {isLoading ? (
              'Loading payment methods...'
            ) : selectedPaymentMethod ? (
              <span>
                {selectedPaymentMethod.AccountName} ({getPaymentMethodTypeLabel(selectedPaymentMethod.PaymentMethodType)})
              </span>
            ) : (
              <span className="text-gray-500">{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>

        {isOpen && (
          <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto">
            <CardContent className="p-2">
              {paymentMethods.length === 0 ? (
                <div className="text-sm text-gray-500 p-2">
                  No payment methods available for this payer
                </div>
              ) : (
                <>
                  {selectedPaymentMethod && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={handleClearSelection}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear selection
                    </Button>
                  )}
                  {paymentMethods.map((paymentMethod) => (
                    <Button
                      key={paymentMethod.PaymentMethodId}
                      type="button"
                      variant="ghost"
                      className={`w-full justify-start ${
                        selectedPaymentMethodId === paymentMethod.PaymentMethodId
                          ? 'bg-blue-50 text-blue-700'
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handlePaymentMethodSelect(paymentMethod)}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      <div className="text-left">
                        <div className="font-medium">{paymentMethod.AccountName}</div>
                        <div className="text-sm text-gray-500">
                          {getPaymentMethodTypeLabel(paymentMethod.PaymentMethodType)} • Priority: {paymentMethod.Priority}
                        </div>
                      </div>
                    </Button>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {error && (
        <p className="text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};
